import { useMutation, useQuery } from "@tanstack/react-query";
import { decodeToken } from "react-jwt";

import { queryClient } from "../../../App";
import { API } from "../apiService";
import {
	getAuthorizationToken,
	hasActiveUserAuthorization,
	isClaimAuthorizationToken,
	isUserAuthorizationExpired,
	isUserAuthorizationToken,
	stripAuthorizationPrefix,
	useAuthorizationToken,
} from "../auth/authToken";
import { buildHeadingPayload } from "./buildHeadingPayload";
import { resumeKeys } from "./resumeKeys";
import { RESUME_ACTIONS, RESUME_SECTIONS } from "./resumeSections";

export function useJobs({ params }) {
	return useQuery({
		queryKey: ["jobs", JSON.stringify(params)],
		queryFn: async () => await API.get("jobs", { params }),
		staleTime: 60 * 1000 * 60,
		retry: 0,
	});
}

export function useJob(jobId) {
	return useQuery({
		queryKey: ["jobs", jobId],
		queryFn: async () => await API.get(`jobs/${jobId}`),
		staleTime: 60 * 1000 * 60,
		enabled: !!jobId,
	});
}

export function prefetchJob(jobId) {
	if (!jobId) return;
	return queryClient.prefetchQuery({
		queryKey: ["jobs", jobId],
		queryFn: async () => await API.get(`jobs/${jobId}`),
		staleTime: 60 * 1000 * 60,
	});
}

export function prefetchJobsByCategory(category) {
	if (!category) return;
	const params = {
		title: "",
		country: "Tanzania",
		region: "",
		city: "",
		jobExpertise: category.toLowerCase() !== "all" ? [category] : [],
		pageNumber: 1,
	};
	return queryClient.prefetchQuery({
		queryKey: ["jobs", JSON.stringify(params)],
		queryFn: async () => await API.get("jobs", { params }),
		staleTime: 60 * 1000 * 60,
	});
}

export function useCompanies() {
	return useQuery({
		queryKey: ["companies"],
		queryFn: async () => await API.get("companies"),
		staleTime: Infinity,
	});
}

export function useCompany(id, options = {}) {
	return useQuery({
		queryKey: ["companies", id],
		queryFn: async () => await API.get(`companies?companyId=${id}`, { authMode: options.authMode }),
		staleTime: Infinity,
		enabled: !!id && (options.enabled ?? true),
	});
}

export function useCompanyJobs(id, options = {}) {
	return useQuery({
		queryKey: ["companies", id, "jobs"],
		queryFn: async () =>
			await (options.authMode === "claim"
				? API.get("job-market", { authMode: options.authMode })
				: API.get(`/jobs/company?companyId=${id}`, { authMode: options.authMode })),
		staleTime: 86400000,
		enabled: !!id && (options.enabled ?? true),
	});
}

export function useCompanyActivities(companyId, params = {}, options = {}) {
	return useQuery({
		queryKey: ["companies", companyId, "activities", params],
		queryFn: async () =>
			await API.get(`companies/${companyId}/activities`, {
				authMode: "claim",
				params,
			}),
		staleTime: 60 * 1000,
		enabled: !!companyId && (options.enabled ?? true),
	});
}

function invalidateCompanyDashboardQueries(companyId) {
	if (!companyId) return;
	queryClient.invalidateQueries({ queryKey: ["companies", companyId] });
	queryClient.invalidateQueries({ queryKey: ["companies", companyId, "jobs"] });
	queryClient.invalidateQueries({ queryKey: ["companies", companyId, "activities"] });
}

export function useUpdateClaimCompanyMutation(companyId, options = {}) {
	return useMutation({
		mutationFn: async (payload) => {
			const { data } = await API.put(`companies/${companyId}`, payload, { authMode: "claim" });
			return data;
		},
		onSuccess: (data) => {
			invalidateCompanyDashboardQueries(companyId);
			options?.onSuccess?.(data);
		},
		onError: (error) => {
			options?.onError?.(error);
		},
	});
}

export function useCreateClaimCompanyJobMutation(companyId, options = {}) {
	return useMutation({
		mutationFn: async (payload) => {
			const { data } = await API.post("job-market", payload, { authMode: "claim" });
			return data;
		},
		onSuccess: (data) => {
			invalidateCompanyDashboardQueries(companyId);
			options?.onSuccess?.(data);
		},
		onError: (error) => {
			options?.onError?.(error);
		},
	});
}

export function useUpdateClaimCompanyJobMutation(companyId, options = {}) {
	return useMutation({
		mutationFn: async ({ id, payload }) => {
			const { data } = await API.put(`job-market/${id}`, payload, { authMode: "claim" });
			return data;
		},
		onSuccess: (data) => {
			invalidateCompanyDashboardQueries(companyId);
			options?.onSuccess?.(data);
		},
		onError: (error) => {
			options?.onError?.(error);
		},
	});
}

export function useDeleteClaimCompanyJobMutation(companyId, options = {}) {
	return useMutation({
		mutationFn: async ({ id }) => {
			const { data } = await API.delete(`job-market/${id}`, { authMode: "claim" });
			return data;
		},
		onSuccess: (data) => {
			invalidateCompanyDashboardQueries(companyId);
			options?.onSuccess?.(data);
		},
		onError: (error) => {
			options?.onError?.(error);
		},
	});
}

export function usePublishClaimCompanyJobMutation(companyId, options = {}) {
	return useMutation({
		mutationFn: async ({ id }) => {
			const { data } = await API.patch(`job-market/${id}`, undefined, { authMode: "claim" });
			return data;
		},
		onSuccess: (data) => {
			invalidateCompanyDashboardQueries(companyId);
			options?.onSuccess?.(data);
		},
		onError: (error) => {
			options?.onError?.(error);
		},
	});
}

export function useExtendClaimCompanyJobDeadlineMutation(companyId, options = {}) {
	return useMutation({
		mutationFn: async ({ id, deadlineDate }) => {
			const { data } = await API.patch(`job-market/${id}/extend-deadline`, { deadlineDate }, { authMode: "claim" });
			return data;
		},
		onSuccess: (data) => {
			invalidateCompanyDashboardQueries(companyId);
			options?.onSuccess?.(data);
		},
		onError: (error) => {
			options?.onError?.(error);
		},
	});
}

export function useMarkClaimCompanyJobFilledMutation(companyId, options = {}) {
	return useMutation({
		mutationFn: async ({ id }) => {
			const { data } = await API.patch(`job-market/${id}/mark-filled`, undefined, { authMode: "claim" });
			return data;
		},
		onSuccess: (data) => {
			invalidateCompanyDashboardQueries(companyId);
			options?.onSuccess?.(data);
		},
		onError: (error) => {
			options?.onError?.(error);
		},
	});
}

export function prefetchCompany(companyId) {
	if (!companyId) return;
	return queryClient.prefetchQuery({
		queryKey: ["companies", companyId],
		queryFn: async () => await API.get(`companies?companyId=${companyId}`),
		staleTime: Infinity,
	});
}

export function prefetchCompanyJobs(companyId) {
	if (!companyId) return;
	return queryClient.prefetchQuery({
		queryKey: ["companies", companyId, "jobs"],
		queryFn: async () => await API.get(`/jobs/company?companyId=${companyId}`),
		staleTime: 86400000,
	});
}

export function useActivelyHiringCompanies() {
	return useQuery({
		queryKey: ["companies", "active"],
		queryFn: async () => await API.get("/jobs/employers-actively-hiring"),
		staleTime: 60 * 1000 * 5,
	});
}

export function useJobCategories() {
	return useQuery({
		queryKey: ["jobs", "categories"],
		queryFn: async () => await API.get("jobs/categories"),
		staleTime: Infinity,
	});
}

export function useLatestJobs() {
	return useQuery({
		queryKey: ["jobs", "latest"],
		queryFn: async () => await API.get("jobs/latest"),
		staleTime: 60 * 1000 * 5,
	});
}

export function useUpcomingJobs() {
	return useQuery({
		queryKey: ["jobs", "upcoming"],
		queryFn: async () => await API.get("jobs/upcoming"),
		staleTime: 60 * 1000 * 5,
	});
}

export function useFeaturedEmployers() {
	return useQuery({
		queryKey: ["employers", "featured"],
		queryFn: async () => await API.get("jobs/employers/featured"),
		staleTime: 60 * 1000,
	});
}

/**
 * Can this visitor read a resume ledger they already hold the id of?
 *
 * Wider than `hasActiveUserAuthorization` on purpose. An anonymous author's
 * `Authorization` cookie is a bare user id with no `CO::`/`GO::`/`LI::` prefix,
 * so the user-token check rejects it — which meant an anonymous visitor who
 * reloaded mid-flow never re-fetched their resume and saw every field blank,
 * even though the id was still in the cookie and the server still had the data.
 *
 * The resume endpoints accept that anonymous id (the builder PATCHes with it on
 * every step), so it is a valid credential here. A company-claim session is not:
 * it authenticates a company, not a resume author.
 */
function hasResumeCredential(token) {
	if (!token) return false;
	if (isClaimAuthorizationToken(token)) return false;
	if (isUserAuthorizationToken(token)) return !isUserAuthorizationExpired(token);
	return true;
}

export function useRecommendedJobs(areasOfExpertise = "") {
	const token = useAuthorizationToken();

	return useQuery({
		queryKey: ["jobs", "recommended"],
		queryFn: async () => {
			const { data } = await API.get("jobs/recommended");
			return data;
		},
		// Short: match scores are recomputed from job preferences on every request, so a stale list
		// misrepresents the profile the moment the user edits it.
		staleTime: 60 * 1000,
		enabled: hasActiveUserAuthorization(token) && !!areasOfExpertise,
		retry: 0,
	});
}

/**
 * Saving preferences changes both the account payload (completeness_pct, Profile Strength) and every
 * recommendation's match score, so both reads are invalidated here rather than at the call site.
 */
export function useUpdateJobPreferences() {
	return useMutation({
		/** @param {Record<string, any>} data Full account payload — see transformToRequestBody. */
		mutationFn: async (data) => {
			const response = await API.put("users/account", data);
			return response.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["user", "account"] });
			queryClient.invalidateQueries({ queryKey: ["jobs", "recommended"] });
		},
	});
}

export function useSavedJobs() {
	const token = useAuthorizationToken();

	return useQuery({
		queryKey: ["jobs", "saved"],
		queryFn: async () => {
			const { data } = await API.get("jobs/saved");
			return data;
		},
		retry: 0,
		staleTime: 60 * 1000 * 60,
		enabled: hasActiveUserAuthorization(token),
	});
}

export function useDeleteJobMutation(id) {
	return useMutation({
		mutationFn: async () => await API.delete(`jobs/${id}`),
		onSuccess: () => {
			queryClient.invalidateQueries(["jobs", "saved", "recommended"]);
		},
	});
}

export const userAccountKey = ["user", "account"];

/**
 * Shape the account read the way every consumer expects it.
 *
 * Exported so a write that already returned the full record can seed the cache with the identical
 * shape, instead of the UI waiting on a re-read.
 */
export function toUserAccount(record) {
	const data = record || {};

	const baseUserData = {
		email: data.emailAddress,
		userID: data._id,
		lastName: data.lastName,
		firstName: data.firstName,
		picture: data.picture,
		isDropzOpsUser: data.isDropzOpsUser || false,
		jobPreferences: data.jobPreferences || {},
		job_preference_progress_status: data.job_preference_progress_status || {},
	};

	// LinkedIn users should use their own profile data
	const authToken = getAuthorizationToken();

	if (authToken?.includes("LI::")) {
		return baseUserData;
	}

	if (data.isDropzOpsUser) {
		localStorage.setItem("isDropzOpsUser", true);
	}

	// For other auth methods (Google, Cognito) the token is the fresher source for the name and
	// avatar, which the backend record may never have been given.
	try {
		const decodedToken = decodeToken(stripAuthorizationPrefix(authToken));
		return {
			...baseUserData,
			lastName: decodedToken?.family_name || baseUserData.lastName,
			firstName: decodedToken?.given_name || baseUserData.firstName,
			picture: decodedToken?.picture || baseUserData.picture,
		};
	} catch (error) {
		console.log(error);
		return baseUserData;
	}
}

/**
 * Publish an account record the app just wrote.
 *
 * `invalidateQueries` alone is not enough after a create. It only marks the entry stale and
 * refetches *active* observers, so the refetch races the navigation that follows it: whichever
 * response lands first wins, and a read that beat the write to the database gets cached as fresh
 * for the full staleTime — a saved profile rendering as 0%. Seeding from the write response is not
 * a race: it is the record the server just returned. The invalidate afterwards is only a
 * background revalidation.
 */
export function primeUserAccount(record) {
	queryClient.setQueryData(userAccountKey, toUserAccount(record));
	queryClient.invalidateQueries({ queryKey: userAccountKey });
}

export function useUserAccount() {
	const token = useAuthorizationToken();

	return useQuery({
		queryKey: userAccountKey,
		queryFn: async () => {
			const { data } = await API.get("users/account");
			return toUserAccount(data);
		},
		retry: 0,
		enabled: hasActiveUserAuthorization(token),
		staleTime: 60 * 30 * 1000,
	});
}

// Resume ledger reads change whenever the user edits a section, so keep them
// fresh on the order of minutes. (The previous `30 * 60 * 1000 * 60` was ~30
// hours — a units bug that made refetch-driven reconciliation effectively never
// happen.)
const RESUME_STALE_TIME = 5 * 60 * 1000;

export function useResumes({ enabled = true } = {}) {
	const token = useAuthorizationToken();

	return useQuery({
		queryKey: ["resumes"],
		queryFn: async () => {
			const { data } = await API.get("resume-ledger");
			return data;
		},
		retry: 0,
		staleTime: RESUME_STALE_TIME,
		// Auth-gated: public pages (jobs, header) consume this too, and a
		// signed-out GET resume-ledger would 401 -> onAuthFailure -> /signin.
		// Gating keeps those pages fully functional with no resume list.
		//
		// `enabled` narrows it further. A token alone is not enough: someone who
		// has just registered holds a valid one but has no backend user record yet
		// — that is exactly why they are sitting on /onboarding — and asking for
		// their ledger there 401s them straight back to /signin.
		enabled: hasActiveUserAuthorization(token) && enabled,
	});
}

export function useResume(id) {
	const token = useAuthorizationToken();

	return useQuery({
		queryKey: ["resume", id],
		queryFn: async () => {
			const { data } = await API.get(`resume-ledger/${id}`);
			return data;
		},
		retry: 0,
		staleTime: RESUME_STALE_TIME,
		// Any credential that can read a ledger, not just a signed-in user's — an
		// anonymous author holds a bare user id and must still get their resume
		// back after a reload. Still gated on `id`, so a visitor with no resume
		// never fires a request and never trips the 401 kick-out.
		enabled: hasResumeCredential(token) && !!id,
	});
}

/**
 * Write one resume section. Replaces `ResumeDataService.patchResumeLedger`.
 *
 * `PATCH resume-ledger/:id` returns the full canonical ledger (backend-verified),
 * so the response is written straight into the detail cache — RouteHandler then
 * re-hydrates the context from it (its revision guard keys off journal
 * .lastUpdatedOn). The list is invalidated because journal metadata changed.
 *
 * mutate({ resumeId, section, data, action = "UPDATE" })
 * - HEADING is reshaped by buildHeadingPayload (template-id encoding etc.).
 * - Use RESUME_SECTIONS / RESUME_ACTIONS for `section` / `action` — never raw
 *   strings (e.g. ACHIEVEMENTS, not ACCOMPLISHMENTS).
 */
export function usePatchResumeSection(options = {}) {
	return useMutation({
		mutationFn: async ({ resumeId, section, data, action = RESUME_ACTIONS.UPDATE }) => {
			// A missing id means the active-resume pointer was lost — most often a
			// session that expired mid-flow (sign-in clears the `resume_id` cookie).
			// Never PATCH `resume-ledger/undefined`: it drops the edit against a
			// non-existent resume and the user's work is lost silently. Fail with the
			// axios-shaped error the section `onError` handlers already read, so the
			// message surfaces. useRecoverActiveResume restores the id shortly after,
			// so a retry succeeds.
			if (!resumeId || resumeId === "undefined" || resumeId === "null") {
				const error = new Error("Missing active resume id");
				error.response = {
					data: { message: "We lost track of your resume. Reopen it from My Resumes and try again." },
				};
				throw error;
			}

			const sectionBody = section === RESUME_SECTIONS.HEADING ? buildHeadingPayload(data) : data;
			const requestBody = { ...sectionBody, section, action };
			const { data: ledger } = await API.patch(`resume-ledger/${resumeId}`, requestBody);
			return ledger;
		},
		onSuccess: (ledger, variables) => {
			// Guard against out-of-order concurrent saves: if two PATCHes resolve
			// in a different order than they were sent, do not let the older
			// response (smaller journal.lastUpdatedOn) clobber the newer one in the
			// cache — otherwise RouteHandler would re-hydrate stale data and
			// silently undo the later save.
			queryClient.setQueryData(resumeKeys.detail(variables.resumeId), (previous) => {
				const previousRevision = previous?.journal?.lastUpdatedOn
					? new Date(previous.journal.lastUpdatedOn).getTime()
					: 0;
				const nextRevision = ledger?.journal?.lastUpdatedOn ? new Date(ledger.journal.lastUpdatedOn).getTime() : 0;
				return nextRevision >= previousRevision ? ledger : previous;
			});
			queryClient.invalidateQueries({ queryKey: resumeKeys.lists() });
			// Refetch the resume itself as well. The PATCH response carries no
			// `journal` at all, so the guard above always keeps the older cached copy
			// and `journal.lastUpdatedOn` never moves — which is exactly the revision
			// RouteHandler keys its re-hydration on. Without this the editing context
			// froze at whatever the first GET returned: saved sections kept showing
			// their old contents and the progress rail kept saying NOT STARTED until
			// the reader reloaded the page by hand.
			queryClient.invalidateQueries({ queryKey: resumeKeys.detail(variables.resumeId) });
			options.onSuccess?.(ledger, variables);
		},
		onError: options.onError,
	});
}

/**
 * Create a resume (POST resume-journal). Replaces `ResumeDataService.createResume`.
 * `data` is the same `{ data: { contactInfo, templateId, yearsOfExperience,
 * resumeTitle } }` shape the HEADING page already builds; it is reshaped by
 * buildHeadingPayload. Returns the created ledger.
 */
export function useCreateResume(options = {}) {
	return useMutation({
		mutationFn: async (data) => {
			const { data: ledger } = await API.post("resume-journal", buildHeadingPayload(data));
			return ledger;
		},
		onSuccess: (ledger, variables) => {
			// Seed the detail cache the same way PATCH does, so RouteHandler hydrates
			// the context from the created ledger — `journal.progressStatus` included.
			// This is the only path that delivers it on the create leg: the backend
			// sets `Authorization` to a bare user id for anonymous builders, which
			// fails `hasActiveUserAuthorization`, so `useResume` stays disabled and
			// never fetches the ledger back. Without this the first section stays
			// un-ticked in the progress rail for the rest of the session.
			if (ledger?._id) {
				queryClient.setQueryData(resumeKeys.detail(ledger._id), ledger);
			}
			queryClient.invalidateQueries({ queryKey: resumeKeys.lists() });
			options.onSuccess?.(ledger, variables);
		},
		onError: options.onError,
	});
}

/**
 * Duplicate a completed resume (POST resume-journal/copyresume). Returns the
 * full copied ledger (backend-verified). Replaces `ResumeDataService.duplicateResume`.
 * mutate({ sourceJournalId, resumeTitle })
 */
export function useDuplicateResume(options = {}) {
	return useMutation({
		mutationFn: async ({ sourceJournalId, resumeTitle }) => {
			const { data: ledger } = await API.post("resume-journal/copyresume", { sourceJournalId, resumeTitle });
			return ledger;
		},
		onSuccess: (ledger, variables) => {
			queryClient.invalidateQueries({ queryKey: resumeKeys.lists() });
			options.onSuccess?.(ledger, variables);
		},
		onError: options.onError,
	});
}

export function useSmartProfile() {
	const token = useAuthorizationToken();

	return useQuery({
		queryKey: ["smartProfile"],
		queryFn: async () => {
			const { data } = await API.get("smart-profiles");
			return data;
		},
		retry: 0,
		staleTime: 30 * 60 * 1000 * 60,
		enabled: hasActiveUserAuthorization(token),
	});
}

export function useDSPInfo(dspId) {
	return useQuery({
		queryKey: ["dspInfo", dspId],
		queryFn: async () => {
			const { data } = await API.get("smart-profiles", {
				params: { dspId: dspId },
			});
			return data;
		},
		retry: 0,
		staleTime: 30 * 60 * 1000 * 60,
		enabled: !!dspId,
	});
}

/**
 * The DDocProcessingStatus enum is exactly `pending` | `success` | `failed`.
 *
 * Anything else — `undefined`, a response that carries no status, a 404 while the row is still
 * settling — is deliberately NOT terminal. Reading "not pending" as success would end the wait on
 * a reply that says nothing at all.
 */
const DSP_EXTRACTION_DONE = ["success"];
const DSP_EXTRACTION_FAILED = ["failed"];

function readExtractionStatus(dspInfo) {
	const status = dspInfo?.processing_status;
	return typeof status === "string" ? status.toLowerCase() : null;
}

export function isExtractionDone(dspInfo) {
	return DSP_EXTRACTION_DONE.includes(readExtractionStatus(dspInfo));
}

export function isExtractionFailed(dspInfo) {
	return DSP_EXTRACTION_FAILED.includes(readExtractionStatus(dspInfo));
}

/**
 * Poll one DSP while the backend extracts it.
 *
 * Separate from `useDSPInfo` on purpose: that hook's staleTime is 30 hours, which would serve the
 * first `pending` response for the whole session. This one keeps nothing.
 */
export function useDSPExtractionStatus(dspId, { enabled = true } = {}) {
	return useQuery({
		queryKey: ["dspInfo", dspId, "polling"],
		queryFn: async () => {
			const { data } = await API.get("smart-profiles", { params: { dspId } });
			return data;
		},
		// A poll every few seconds is its own retry; react-query's backoff would only make the
		// interval unpredictable.
		retry: 0,
		staleTime: 0,
		refetchInterval: (query) => {
			const dspInfo = query.state.data;
			return isExtractionDone(dspInfo) || isExtractionFailed(dspInfo) ? false : 3000;
		},
		enabled: enabled && !!dspId,
	});
}

/**
 * Find a DSP by the hash of the file that created it.
 *
 * `POST smart-profiles` is only known to return the presigned upload details, so the new dsp_id
 * may not come back with it. The hash is unique — the API rejects a duplicate — where the title
 * is not, which makes it the only safe way to identify the row we just wrote.
 */
export async function findDspIdByFileHash(fileHash) {
	if (!fileHash) return null;

	const { data } = await API.get("smart-profiles");

	const match = (data?.dsps ?? []).find((dsp) => (dsp.dsp_docs ?? []).some((doc) => doc.file_hash === fileHash));

	return match?.dsp_id ?? null;
}

export function useCreateSmartProfile(options) {
	return useMutation({
		mutationFn: async (payload) => {
			const { data: response } = await API.post("smart-profiles", payload);
			return response;
		},
		onSuccess: (data) => {
			queryClient.invalidateQueries(["smartProfile"]);
			options?.onSuccess?.(data);
		},
		onError: (error) => {
			options?.onError?.(error);
		},
	});
}

export function useUpdateSmartProfile(options) {
	return useMutation({
		mutationFn: async (payload) => {
			const _data = { ...payload };
			const dspId = payload.dspId;
			delete _data.dspId;

			const { data: response } = await API.patch(`smart-profiles/${dspId}`, _data);
			return response;
		},
		onSuccess: (data) => {
			queryClient.invalidateQueries(["smartProfile", ["dspInfo"]]);
			options?.onSuccess?.(data);
		},
		onError: (error) => {
			options?.onError?.(error);
		},
	});
}

export function useDeleteDocument(options) {
	return useMutation({
		mutationFn: async (payload) => {
			const params = {
				docId: payload.doc_id,
				docType: payload.doc_type,
				dspId: payload.dspId,
			};
			return await API.delete(`smart-profiles/docs/${payload.doc_id}`, {
				params,
			});
		},
		onSuccess: (data) => {
			queryClient.invalidateQueries(["smartProfile", "dspInfo"]);
			options?.onSuccess?.(data);
		},
		onError: (error) => {
			options?.onError?.(error);
		},
	});
}

export function useApplications() {
	return useQuery({
		queryKey: ["applications"],
		queryFn: async () => {
			const { data } = await API.get("job-applications");
			return data;
		},
		staleTime: 60 * 1000 * 60,
		retry: 0,
	});
}

export function useUpdateApplicationStatus({ onSuccess, onError }) {
	return useMutation({
		mutationFn: async ({ applicationId, payload }) => {
			const { data: response } = await API.patch(`job-applications/application/${applicationId}`, payload);
			return response;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["applications"] });
			if (onSuccess) {
				onSuccess();
			}
		},
		onError: (error) => {
			onError(error);
		},
	});
}

export function useUpdateApplicationStage({ onSuccess, onError }) {
	return useMutation({
		mutationFn: async ({ applicationId, stageId, payload }) => {
			const { data: response } = await API.patch(
				`job-applications/application/${applicationId}/stage/${stageId}`,
				payload,
			);
			return response;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["applications"] });
			if (onSuccess) {
				onSuccess();
			}
		},
		onError: (error) => {
			onError(error);
		},
	});
}

export function useCreateApplication({ onSuccess, onError }) {
	return useMutation({
		mutationFn: async ({ payload }) => {
			const { data: response } = await API.post("job-applications", payload);
			return response;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["applications"] });
			if (onSuccess) {
				onSuccess();
			}
		},
		onError: (error) => {
			onError(error);
		},
	});
}
